import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const stages = [
    { t: "Procurement Plan", d: "Annual planning aligned to budget." },
    { t: "Procurement Request", d: "Requisitions with approvals & routing." },
    { t: "Tender / Solicitation", d: "Publish tenders, docs, invitations." },
    { t: "Bid Submission", d: "Secure supplier bid tracking." },
    { t: "Opening & Evaluation", d: "Committee-driven scoring & reports." },
    { t: "Approval", d: "Multi-level authorization workflows." },
    { t: "Award", d: "Notification and contracting stage." },
    { t: "Contract Execution", d: "Milestones, deliverables & payments." },
    { t: "Performance Monitoring", d: "SLAs, KPIs and supplier scoring." },
    { t: "Contract Closure", d: "Final handover, audit trail complete." },
];

export default function HowItWorks() {
    return (
        <section id="workflow" data-testid="how-it-works" className="bg-[#0B1930] text-white relative overflow-hidden">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-36 relative">
                <div className="grid lg:grid-cols-12 gap-8 mb-20">
                    <div className="lg:col-span-6">
                        <div className="eyebrow">Featured Workflow</div>
                        <h2 className="headline text-4xl md:text-5xl lg:text-6xl mt-4">
                            End-to-end <br /> procurement lifecycle.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <p className="text-white/70 text-lg leading-relaxed">
                            One traceable thread from procurement planning through contract closure.
                            Every stage governed, notified and audit-ready.
                        </p>
                    </div>
                </div>

                <div className="relative">
                    <div className="absolute left-0 right-0 top-8 h-px bg-white/10 hidden lg:block" />
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 gap-y-14">
                        {stages.map((s, i) => (
                            <motion.div
                                key={s.t}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="relative"
                            >
                                <div className="relative flex items-center gap-3 mb-4">
                                    <div className="w-4 h-4 rounded-full border-2 border-[#E8B923] bg-[#0B1930] relative z-10" />
                                    <span className="font-mono text-[10px] text-white/40 tracking-widest">STEP {String(i + 1).padStart(2, "0")}</span>
                                </div>
                                <h3 className="font-serif text-lg text-white leading-tight">{s.t}</h3>
                                <p className="text-white/50 text-xs mt-2 leading-relaxed">{s.d}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="text-white/60 text-sm">Complete audit trail from initiation through contract closure.</p>
                    <Link to="/products/procurement" data-testid="how-cta" className="btn-gold text-sm">
                        Explore Procurement <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
